import type { BooruPost } from "./booru-api"

// Tag categories with priority weights
const TAG_CATEGORIES = {
  character: { priority: 3, keywords: ["character:", "char:"] },
  artist: { priority: 3, keywords: ["artist:", "by_", "drawn_by"] },
  series: { priority: 2, keywords: ["series:", "franchise:"] },
  copyright: { priority: 2, keywords: ["copyright:"] },
  species: { priority: 1.5, keywords: ["species:"] },
  general: { priority: 1, keywords: [] },
}

function getTagCategory(tag: string): string {
  for (const [category, config] of Object.entries(TAG_CATEGORIES)) {
    if (config.keywords.some((keyword) => tag.includes(keyword))) {
      return category
    }
  }
  return "general"
}

function scoreTag(tag: string, frequency: number, totalPosts: number): number {
  const category = getTagCategory(tag)
  const categoryPriority = TAG_CATEGORIES[category as keyof typeof TAG_CATEGORIES]?.priority || 1
  const frequencyScore = frequency / totalPosts
  return frequencyScore * categoryPriority
}

/** A search asks for posts with ALL of its tags, so more tags means fewer results; past a handful it finds nothing.
 * Danbooru turns away anonymous searches of more than two tags outright. */
export function maxSimilarTags(site?: string): number {
  return site === "danbooru" ? 2 : 6
}

/** The tags to search to find posts like this one. When the post carries the source's own tag types (Danbooru, Rule34 and
 * Gelbooru do), the artist, character and series tags come first (that is what makes posts alike), meta tags such as
 * "highres" are left out, and the rest is filled with the post's most common tags. Otherwise the tag names are guessed. */
export function extractSimilarTags(post: BooruPost, allPosts: BooruPost[]): string[] {
  if (!post.tags || post.tags.length === 0) return []

  const limit = maxSimilarTags(post.site)
  const tagCounts = new Map<string, number>()
  const totalPosts = Math.max(1, allPosts.length)

  // Analyze all posts to get tag frequencies
  for (const p of allPosts) {
    for (const tag of p.tags) {
      tagCounts.set(tag, (tagCounts.get(tag) || 0) + 1)
    }
  }

  const postTags = Array.from(new Set(post.tags))
  const types = post.tagCategories
  const byFrequency = (a: string, b: string) => (tagCounts.get(b) || 0) - (tagCounts.get(a) || 0)

  if (types && Object.values(types).some((type) => type !== "general")) {
    const ofType = (...wanted: string[]) => postTags.filter((tag) => wanted.includes(types[tag])).sort(byFrequency)
    const general = postTags.filter((tag) => (types[tag] ?? "general") === "general").sort(byFrequency)
    // One of each kind in turn (artist, character, series), then a second character, then general tags. With few slots
    // (Danbooru allows two) that gives artist + character, or character + series when there is no artist, rather than two
    // characters that are often variants of each other.
    const characters = ofType("character")
    const picked = [...ofType("artist").slice(0, 1), ...characters.slice(0, 1), ...ofType("copyright").slice(0, 1), ...characters.slice(1, 2), ...general]
    return picked.slice(0, limit)
  }

  const scoredTags = postTags
    .map((tag) => ({
      tag,
      score: scoreTag(tag, tagCounts.get(tag) || 0, totalPosts),
    }))
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((t) => t.tag)

  // If we have less than 3 important tags, add more generic tags
  if (scoredTags.length < 3) {
    const additionalTags = postTags.filter((tag) => !scoredTags.includes(tag)).slice(0, limit - scoredTags.length)
    return [...scoredTags, ...additionalTags]
  }

  return scoredTags
}
